import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { JournalEntryInput, JournalEntryPatch } from "@gb/contracts";
import { makeThumbnail } from "@/lib/image-thumbnail";
import { api, errorMessage } from "../../api/client";

const base = (tenantId: string, projectId: string) =>
  ["tenants", tenantId, "projects", projectId, "journal"] as const;

export function useJournalDays(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: base(tenantId, projectId),
    queryFn: async () => {
      const res = await api.journals.days({ params: { tenantId, projectId }, query: {} });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

export function useJournalEntry(tenantId: string, projectId: string, date: string) {
  return useQuery({
    queryKey: [...base(tenantId, projectId), date],
    retry: false,
    queryFn: async () => {
      const res = await api.journals.entry({ params: { tenantId, projectId, date } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.entry;
    },
  });
}

export function useJournalNotes(tenantId: string, projectId: string, date: string) {
  return useQuery({
    queryKey: [...base(tenantId, projectId), date, "notes"],
    queryFn: async () => {
      const res = await api.journals.notes({ params: { tenantId, projectId, date } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.notes;
    },
  });
}

export function useJournalPhotos(tenantId: string, projectId: string, date: string) {
  return useQuery({
    queryKey: [...base(tenantId, projectId), date, "photos"],
    queryFn: async () => {
      const res = await api.journals.photos({ params: { tenantId, projectId, date } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.photos;
    },
  });
}

export function useDictations(tenantId: string, projectId: string) {
  return useQuery({
    queryKey: [...base(tenantId, projectId), "dictations"],
    queryFn: async () => {
      const res = await api.journals.dictations({
        params: { tenantId, projectId },
        query: { limit: 50, offset: 0 },
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

/** Everything under the project's journal — days list and every day's detail. */
function useInvalidateJournal(tenantId: string, projectId: string) {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: base(tenantId, projectId) });
}

export function useCreateJournalEntry(tenantId: string, projectId: string) {
  const invalidate = useInvalidateJournal(tenantId, projectId);
  return useMutation({
    mutationFn: async (input: JournalEntryInput) => {
      const res = await api.journals.createEntry({ params: { tenantId, projectId }, body: input });
      if (res.status !== 201) throw new Error(errorMessage(res));
      return res.body.entry;
    },
    onSuccess: invalidate,
  });
}

export function usePatchJournalEntry(tenantId: string, projectId: string) {
  const invalidate = useInvalidateJournal(tenantId, projectId);
  return useMutation({
    mutationFn: async ({ entryId, patch }: { entryId: string; patch: JournalEntryPatch }) => {
      const res = await api.journals.patchEntry({
        params: { tenantId, projectId, entryId },
        body: patch,
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.entry;
    },
    onSuccess: invalidate,
  });
}

export function useApproveJournalEntry(tenantId: string, projectId: string) {
  const invalidate = useInvalidateJournal(tenantId, projectId);
  return useMutation({
    mutationFn: async ({ entryId, sentTo }: { entryId: string; sentTo?: string[] }) => {
      const res = await api.journals.approveEntry({
        params: { tenantId, projectId, entryId },
        body: sentTo && sentTo.length > 0 ? { sentTo } : {},
      });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body.entry;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteJournalEntry(tenantId: string, projectId: string) {
  const invalidate = useInvalidateJournal(tenantId, projectId);
  return useMutation({
    mutationFn: async (entryId: string) => {
      const res = await api.journals.deleteEntry({ params: { tenantId, projectId, entryId } });
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
    onSuccess: invalidate,
  });
}

/**
 * One photograph, the console's single-PUT way: downscale in the browser,
 * presign, PUT the original (and the thumbnail when one could be made — both
 * plain fetch, no bearer header, it is S3), then complete. Description runs
 * out of band; the invalidated listing reports `descriptionStatus`.
 */
export function useUploadJournalPhoto(tenantId: string, projectId: string) {
  const invalidate = useInvalidateJournal(tenantId, projectId);
  return useMutation({
    mutationFn: async ({ entryId, file }: { entryId: string; file: File }) => {
      const thumbnail = await makeThumbnail(file);
      const presigned = await api.journals.photoUploadUrl({
        params: { tenantId, projectId, entryId },
        body: {
          filename: file.name,
          mimeType: file.type || "application/octet-stream",
          sizeBytes: file.size,
          widthPx: thumbnail?.widthPx,
          heightPx: thumbnail?.heightPx,
          withThumbnail: Boolean(thumbnail),
        },
      });
      if (presigned.status !== 201) throw new Error(errorMessage(presigned));

      const put = await fetch(presigned.body.uploadUrl, {
        method: "PUT",
        headers: { "content-type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!put.ok) throw new Error(`The upload didn't finish (${put.status}).`);

      if (thumbnail && presigned.body.thumbnailUploadUrl) {
        // Best-effort: the original is what is stored and described; a failed
        // downscale PUT costs a grid placeholder, not the photograph.
        await fetch(presigned.body.thumbnailUploadUrl, {
          method: "PUT",
          headers: { "content-type": "image/jpeg" },
          body: thumbnail.blob,
        }).catch(() => undefined);
      }

      const completed = await api.journals.completePhoto({
        params: { tenantId, projectId, entryId, photoId: presigned.body.photoId },
        body: {},
      });
      if (completed.status !== 200) throw new Error(errorMessage(completed));
      return completed.body.photo;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteJournalPhoto(tenantId: string, projectId: string) {
  const invalidate = useInvalidateJournal(tenantId, projectId);
  return useMutation({
    mutationFn: async ({ entryId, photoId }: { entryId: string; photoId: string }) => {
      const res = await api.journals.deletePhoto({
        params: { tenantId, projectId, entryId, photoId },
      });
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
    onSuccess: invalidate,
  });
}
